<template>
    <form
        method="post"
        name="frm"
        class="space-y-4"
        @submit.prevent="sendMessage"
    >
        <UFormField
            label="Name"
            required
        >
            <UInput
                v-model="name"
                class="w-full"
            />
            <div
                v-if="errorName"
                class="text-primary text-xs"
            >
                Bitte geben Sie Ihren Namen ein!
            </div>
        </UFormField>
        <UFormField label="Firma">
            <UInput
                v-model="company"
                class="w-full"
            />
        </UFormField>
        <UFormField
            label="E-Mail"
            required
        >
            <UInput
                v-model="email"
                type="email"
                class="w-full"
            />
            <div
                v-if="errorEmail"
                class="text-primary text-xs"
            >
                Bitte geben Sie Ihre E-Mail-Adresse ein!
            </div>
        </UFormField>
        <UFormField label="Adresse">
            <UInput
                v-model="street"
                class="w-full"
            />
        </UFormField>

        <div class="flex flex-row gap-x-4">
            <UFormField
                label="PLZ"
                class="w-1/4"
            >
                <UInput
                    v-model="zip"
                />
            </UFormField>
            <UFormField
                label="Ort"
                class="w-full"
            >
                <UInput
                    v-model="city"
                    class="w-full"
                />
            </UFormField>
        </div>
        <UFormField
            label="Ihre Nachricht"
            required
        >
            <UTextarea
                v-model="message"
                class="w-full"
            />
            <div
                v-if="errorMessage"
                class="text-primary text-xs"
            >
                Bitte geben Sie Ihre Nachricht ein!
            </div>
        </UFormField>

        <UCheckbox
            v-model="privacy"
            required
            style="padding-inline: 0; padding-block: 0;"
        >
            <template #label>
                Ich habe die <a
                href="/Datenschutz/"
                target="_blank"
            >Datenschutzhinweise</a> zur Kenntnis genommen.
            </template>
        </UCheckbox>
        <div
            v-if="errorPrivacy"
            class="text-primary text-xs"
        >
            Sie müssen bestätigen, dass Sie die Datenschutzhinweise zur Kenntnis genommen haben!
        </div>

        <UCheckbox
            v-model="recaptchaConsent"
            required
            style="padding-inline: 0; padding-block: 0;"
        >
            <template #label>
                Ich willige ein, dass zur Vermeidung von Spam der Dienst Google reCAPTCHA eingesetzt wird. Dabei können
                personenbezogene Daten an Google übermittelt werden.
            </template>
        </UCheckbox>
        <div
            v-if="errorRecaptchaConsent"
            class="text-primary text-xs"
        >
            Sie müssen der Nutzung von Google reCAPTCHA zum Spam-Schutz einwilligen!
        </div>

        <UCheckbox
            v-model="infoText"
            style="padding-inline: 0; padding-block: 0;"
        >
            <template #label>
                Wenn Sie eine schnelle und detailliertere Rückmeldung wünschen, bitten wir Sie Ihre Adresse
                und PLZ freizugeben. Diese Informationen erleichtern uns ebenfalls eine genauere Berechnung
                der Fracht- und Montagekosten. Mit der Bestätigung dieses Hinweises erteilen Sie hiermit die
                Erlaubnis der Weiterverarbeitung Ihrer Adressdaten.
            </template>
        </UCheckbox>

        <p class="mt-2 text-xs!">
            Diese Seite ist durch reCAPTCHA geschützt und es gelten die Google <a
            href="https://policies.google.com/privacy"
            rel="noopener noreferrer"
            target="_blank"
        >Datenschutzbestimmungen</a> und <a
            href="https://policies.google.com/terms"
            rel="noopener noreferrer"
            target="_blank"
        >Nutzungsbedingungen</a> .
        </p>

        <div class="requiredNotice">
            * Pflichtfelder
        </div>
      <UButton
            type="submit"
            class="w-full justify-center"
            trailing-icon="i-lucide-send"
        >
            Absenden
      </UButton>
    </form>
</template>

<script setup lang="ts">
const {public: pub} = useRuntimeConfig();
const toast = useToast();

const MIN_RECAPTCHA_SCORE = 0.5;
const email = ref('');
const name = ref('');
const company = ref('');
const street = ref('');
const zip = ref('');
const city = ref('');
const message = ref('');
const privacy = ref(false);
const infoText = ref(false);
const recaptchaConsent = ref(false);

const errorName = ref(false);
const errorEmail = ref(false);
const errorMessage = ref(false);
const errorPrivacy = ref(false);
const errorRecaptchaConsent = ref(false);

const {execute: executeRecaptcha} = useRecaptcha();

async function validation() {
    if (name.value === '') {
        errorName.value = true;
        return false;
    }
    errorName.value = false;

    if (email.value === '') {
        errorEmail.value = true;
        return false;
    }
    errorEmail.value = false;

    if (message.value === '') {
        errorMessage.value = true;
        return false;
    }
    errorMessage.value = false;

    if (!privacy.value) {
        errorPrivacy.value = true;
        return false;
    }
    errorPrivacy.value = false;

    if (!recaptchaConsent.value) {
        errorRecaptchaConsent.value = true;
        return false;
    }

    const recaptchaToken = await executeRecaptcha('contact_form');
    const formData = new FormData();
    formData.append('recaptchaToken', recaptchaToken);
    const response = await fetch('/api/render-email', {
        method: 'POST',
        body: formData,
    });

    return response.ok;
}

async function sendMessage() {
    if (await validation()) {
        const response = await $fetch<{ sent: boolean }>(`${pub.apiUrl}/api/contact`, {
            method: 'POST',
            body: {
                mode: 'contact',
                phone: '',
                zip: zip.value,
                article: '',
                email: email.value,
                name: name.value,
                company: company.value,
                street: street.value,
                city: city.value,
                message: message.value,
                infoText: infoText.value,
            },
        });

        if (response.sent) {
            toast.add({
                title: 'Erfolgreich versendet',
                description: 'Ihre Nachricht wurde erfolgreich versendet.',
                color: 'success',
            });

            email.value = '';
            name.value = '';
            company.value = '';
            street.value = '';
            zip.value = '';
            city.value = '';
            message.value = '';
            privacy.value = false;
            infoText.value = false;
            recaptchaConsent.value = false;
        }
    }
}
</script>

<style scoped>

</style>
